import React from 'react';
import { Loader, CheckCircle, XCircle, Clock, Bot } from 'lucide-react';

const getStatusIcon = (status) => {
  switch (status) {
    case 'complete':
      return <CheckCircle size={16} className="text-green-500" />;
    case 'executing':
      return <Loader size={16} className="animate-spin text-blue-500" />;
    case 'error':
      return <XCircle size={16} className="text-red-500" />;
    default:
      return <Clock size={16} className="text-gray-500" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'complete':
      return 'text-green-600';
    case 'executing':
      return 'text-blue-600';
    case 'error':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
};

/**
 * Renders an interleaved activity feed (text + tool steps in order).
 * Used both during progress and as the final message content.
 */
export const ActivityFeed = ({ activity }) => {
  if (!activity || activity.length === 0) return null;

  return (
    <div className="chat-activity-feed">
      {activity.map((entry, index) => {
        if (entry.type === 'text') {
          return (
            <div key={index} className="chat-activity-text">
              {entry.content}
            </div>
          );
        }
        if (entry.type === 'step') {
          return (
            <div key={index} className="chat-progress-step">
              {getStatusIcon(entry.status)}
              <span
                className={`chat-progress-step-name ${getStatusColor(entry.status)}`}
              >
                {entry.friendlyName || entry.name}
              </span>
              {entry.resultSummary && (
                <span className="chat-progress-step-result">
                  {entry.resultSummary}
                </span>
              )}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const ChatProgress = ({ progress }) => {
  if (!progress) return null;

  const isComplete = progress.status === 'complete';

  // When complete, render just the activity feed (like a regular message)
  if (isComplete) {
    return (
      <div className="chat-message assistant">
        <div className="chat-message-avatar">
          <Bot size={16} />
        </div>
        <div className="chat-message-content">
          <ActivityFeed activity={progress.activity} />
        </div>
      </div>
    );
  }

  // While in progress, show spinner + progress bar + activity
  return (
    <div className="chat-progress">
      <div className="chat-progress-header">
        <Loader size={20} className="animate-spin" />
        <span className="chat-progress-status text-blue-600">
          {progress.message || 'Processing...'}
        </span>
      </div>

      {progress.progress > 0 && (
        <div className="chat-progress-bar">
          <div
            className="chat-progress-bar-fill"
            style={{ width: `${progress.progress}%` }}
          />
        </div>
      )}

      <ActivityFeed activity={progress.activity} />
    </div>
  );
};

export default ChatProgress;
